// "Keep under N KB" (FR-21, FR-22): binary search on JPG/WebP quality.

export type QualityResult = {
  blob: Blob;
  quality: number;
  /** False when even the lowest quality is still too big. */
  fits: boolean;
};

const MIN_QUALITY = 0.05;
const MAX_QUALITY = 0.95;

/** Finds the highest quality whose file is at most `maxBytes`.
 *  `encode` is called about 7 times; it must be deterministic. */
export async function searchQuality(
  encode: (quality: number) => Promise<Blob>,
  maxBytes: number,
  { min = MIN_QUALITY, max = MAX_QUALITY, steps = 7 } = {},
): Promise<QualityResult> {
  const top = await encode(max);
  if (top.size <= maxBytes) return { blob: top, quality: max, fits: true };

  let best: QualityResult | null = null;
  let lo = min;
  let hi = max;
  for (let i = 0; i < steps; i++) {
    const q = (lo + hi) / 2;
    const blob = await encode(q);
    if (blob.size <= maxBytes) {
      best = { blob, quality: q, fits: true };
      lo = q;
    } else {
      hi = q;
    }
  }
  if (best) return best;

  const bottom = await encode(min);
  return { blob: bottom, quality: min, fits: bottom.size <= maxBytes };
}
